import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { BuyerContact, CollectionActivityWithUser } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getUserCustomer } from '../../../../redux/services/suppliersService';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';
import { decideTaxExemptReview } from '../../../../redux/services/creditApplicationsService';
import { openDillToast } from '../../../../utils/helpers';

interface ViewTaskModalProps {
  open: boolean;
  activity: CollectionActivityWithUser | null;
  onClose: () => void;
}

const PRIORITY_LABELS: Record<string, string> = {
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
};

const PRIORITY_STYLES: Record<string, string> = {
  HIGH: 'bg-red-50 text-red-700',
  MEDIUM: 'bg-amber-50 text-amber-700',
  LOW: 'bg-green-50 text-green-700',
};

const TASK_TYPE_LABELS: Record<string, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  TEXT_MESSAGE: 'Text',
  TO_DO: 'To-Do',
};

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-600',
  IN_PROGRESS: 'bg-blue-50 text-blue-700',
  COMPLETED: 'bg-green-50 text-green-700',
  CANCELLED: 'bg-red-50 text-red-600',
};

function ViewTaskModal({ open, activity, onClose }: ViewTaskModalProps) {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);

  const [buyerUsers, setBuyerUsers] = useState<
    {
      email: string;
      firstName: string;
      lastName: string;
      userId: string;
      isInvitedUser: boolean;
    }[]
  >([]);
  const [isLoadingBuyer, setIsLoadingBuyer] = useState(false);
  const [buyerContacts, setBuyerContacts] = useState<BuyerContact[]>([]);

  const fetchBuyerDetails = useCallback(
    async (buyerId: string) => {
      if (!currentSupplier?.id) return;
      setIsLoadingBuyer(true);
      try {
        const result = await dispatch(getUserCustomer({ supplierId: currentSupplier.id, buyerId }));
        if (result.type === 'supplier/getUserCustomer/fulfilled') {
          const payload = result.payload as any;
          if (payload?.data?.usersEmailsDetails) {
            setBuyerUsers(payload.data.usersEmailsDetails);
          }
          if (payload?.data?.buyer?.buyerContacts) {
            setBuyerContacts(payload.data.buyer.buyerContacts);
          }
        }
      } catch (error) {
        console.error('Failed to fetch buyer details:', error);
      } finally {
        setIsLoadingBuyer(false);
      }
    },
    [dispatch, currentSupplier?.id]
  );

  useEffect(() => {
    // Credit application tasks (e.g. tax exemption review) aren't tied to a customer record.
    if (open && activity?.buyerId) {
      fetchBuyerDetails(activity.buyerId);
    }
  }, [open, activity, fetchBuyerDetails]);

  const taskDetails = activity?.taskDetails;
  const priority = taskDetails?.priority || 'MEDIUM';
  const status = taskDetails?.status || 'PENDING';
  const taskType = (taskDetails as any)?.taskType || 'TO_DO';

  const assignedToName = useMemo(() => {
    if (!taskDetails?.assignedTo) return 'Unassigned';
    const match = buyerUsers.find((u) => u.userId === taskDetails.assignedTo);
    if (match) return `${match.firstName || ''} ${match.lastName || ''}`.trim() || match.email;
    if ((activity as any)?.assignedToUser) {
      const u = (activity as any).assignedToUser;
      return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || u.id;
    }
    return taskDetails.assignedTo;
  }, [taskDetails?.assignedTo, buyerUsers, activity]);

  const contactReferences = useMemo(() => {
    const contactRefs = activity?.references?.filter((r) => r.type === 'CONTACT') ?? [];
    if (contactRefs.length === 0) return null;
    return contactRefs.map((r) => r.description).join(', ');
  }, [activity?.references]);

  const otherReferences = useMemo(() => {
    const otherRefs = activity?.references?.filter((r) => r.type !== 'CONTACT') ?? [];
    if (otherRefs.length === 0) return null;
    return otherRefs.map((r) => `${r.type}: ${r.description}`).join(', ');
  }, [activity?.references]);

  const descriptionText = useMemo(() => {
    if (!taskDetails?.description) return null;
    const desc = taskDetails.description;
    try {
      const parsed = typeof desc === 'string' ? JSON.parse(desc) : desc;
      if (typeof parsed === 'object' && parsed !== null) {
        const root = (parsed as any).root;
        if (root?.children) {
          return root.children
            .map((node: any) => node.children?.map((child: any) => child.text || '').join(''))
            .filter(Boolean)
            .join('\n');
        }
        return JSON.stringify(parsed);
      }
      return String(desc);
    } catch {
      return String(desc);
    }
  }, [taskDetails?.description]);

  const [decidingTaxExempt, setDecidingTaxExempt] = useState<'EXEMPT' | 'NOT_EXEMPT' | null>(null);
  const isTaxExemptReview = taskDetails?.taskCategory === 'TAX_EXEMPT_REVIEW';
  const taxExemptDecision = taskDetails?.taxExemptDecision;

  const handleTaxExemptDecision = async (decision: 'EXEMPT' | 'NOT_EXEMPT') => {
    if (!currentSupplier?.id || !taskDetails?.creditApplicationId) return;
    setDecidingTaxExempt(decision);
    const result = await dispatch(
      decideTaxExemptReview({
        supplierId: currentSupplier.id,
        creditApplicationId: taskDetails.creditApplicationId,
        decision,
      })
    );
    setDecidingTaxExempt(null);
    if (result.type.endsWith('/fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Tax exemption review saved.' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save tax exemption review.' });
    }
  };

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <DillModal
      open={open}
      onClose={onClose}
      title="Task Details"
      maxWidth="480px"
      footer={
        <div className="flex justify-end">
          <DillButton onClick={onClose} variant="secondary" label="Close" />
        </div>
      }>
      {/* Title — the identity of this task */}
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Task</p>
        <p className="text-sm font-semibold text-gray-900 mt-0.5">
          {taskDetails?.title || 'Untitled'}
        </p>
      </div>

      {/* Status, priority, type, due date */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 mt-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Status</p>
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${
              STATUS_STYLES[status] || STATUS_STYLES.PENDING
            }`}>
            {STATUS_LABELS[status] || 'Pending'}
          </span>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Priority</p>
          <span
            className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${
              PRIORITY_STYLES[priority] || PRIORITY_STYLES.MEDIUM
            }`}>
            {PRIORITY_LABELS[priority] || 'Medium'}
          </span>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Type</p>
          <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mt-0.5">
            {TASK_TYPE_LABELS[taskType] || 'To-Do'}
          </span>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Due date</p>
          <p className="text-sm font-medium text-gray-900 mt-0.5">
            {taskDetails?.dueDate ? formatDate(taskDetails.dueDate) : '-'}
          </p>
        </div>
      </div>

      {/* Description */}
      {descriptionText && (
        <div className="border-t border-gray-100 mt-4 pt-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Description</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {descriptionText}
          </p>
        </div>
      )}

      {isTaxExemptReview && (
        <div className="border-t border-gray-100 mt-4 pt-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[11px] uppercase tracking-wide text-gray-400">
              Tax exemption review
            </p>
            {currentSupplier?.id && taskDetails?.creditApplicationId && (
              <a
                href={`/supplier/${currentSupplier.id}/creditApplications/${taskDetails.creditApplicationId}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs font-semibold PRIMARY_500-CLR hover:underline">
                View credit application
              </a>
            )}
          </div>
          {taxExemptDecision ? (
            <p
              className={`mt-2 inline-flex rounded-md px-2 py-1 text-xs font-medium ${
                taxExemptDecision === 'EXEMPT'
                  ? 'bg-green-50 text-green-700'
                  : 'bg-red-50 text-red-700'
              }`}>
              {taxExemptDecision === 'EXEMPT'
                ? 'Confirmed: customer is tax exempt'
                : 'Confirmed: customer is not tax exempt'}
            </p>
          ) : (
            <>
              <p className="text-sm text-gray-700 mt-1">
                Review the customer&apos;s tax exemption file on the credit application, then
                confirm:
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                <DillButton
                  label={
                    decidingTaxExempt === 'EXEMPT' ? 'Saving...' : 'Yes, customer is tax exempt'
                  }
                  onClick={() => handleTaxExemptDecision('EXEMPT')}
                  isDisabled={!!decidingTaxExempt}
                />
                <DillButton
                  variant="secondary"
                  label={
                    decidingTaxExempt === 'NOT_EXEMPT'
                      ? 'Saving...'
                      : 'No, customer is not tax exempt'
                  }
                  onClick={() => handleTaxExemptDecision('NOT_EXEMPT')}
                  isDisabled={!!decidingTaxExempt}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* Customer & assignment */}
      <div className="border-t border-gray-100 mt-4 pt-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400">Customer</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5">
              {activity?.buyer?.name || '-'}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide text-gray-400">Assigned to</p>
            <p className="text-sm font-medium text-gray-900 mt-0.5">{assignedToName}</p>
          </div>
        </div>
      </div>

      {/* Completion */}
      {taskDetails?.completedAt && (
        <div className="border-t border-gray-100 mt-4 pt-4">
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Completed</p>
          <p className="text-sm font-medium text-gray-900 mt-0.5">
            {formatDate(taskDetails.completedAt)}
          </p>
        </div>
      )}

      {/* References */}
      {(contactReferences || otherReferences) && (
        <div className="border-t border-gray-100 mt-4 pt-4">
          {contactReferences && (
            <div>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">Contacts</p>
              <p className="text-sm text-gray-700 mt-0.5">{contactReferences}</p>
            </div>
          )}
          {otherReferences && (
            <div className={contactReferences ? 'mt-3' : ''}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">References</p>
              <p className="text-sm text-gray-700 mt-0.5">{otherReferences}</p>
            </div>
          )}
        </div>
      )}
    </DillModal>
  );
}

export default ViewTaskModal;
